import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import trpc from "@/shared/utils/trpc";
import { COOKIE_KEYS } from "@/shared/constants/cookies.const";
import Cookies from "js-cookie";
import { Prisma } from "@gt/prisma";
import { useNavigate, useLocation } from "react-router-dom";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { invalidateClientSession, isInvalidSessionTrpcError } from "@/shared/utils/authSession.util";
import {
  readGuestShortlistCompanyIds,
  readGuestShortlistEcosystemHeadUserIds,
  readGuestShortlistExpertUserIds,
  removeGuestShortlistCompanyIds,
  removeGuestShortlistEcosystemHeadUserIds,
  removeGuestShortlistExpertUserIds,
} from "@/shared/utils/guestShortlistStorage";
import { hasClientCreatedProfile } from "@/shared/constants/clientOnboarding.const";
import { pendingRequestsReceivedBaselineKey } from "@/shared/constants/pendingRequestsSpotlight.const";
import { jwtDecode } from "jwt-decode";

function readPendingRequestsBaseline(userId: string): number | null {
  try {
    const raw = localStorage.getItem(pendingRequestsReceivedBaselineKey(userId));
    if (raw === null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writePendingRequestsBaseline(userId: string, received: number) {
  try {
    localStorage.setItem(pendingRequestsReceivedBaselineKey(userId), String(received));
  } catch {
    /* ignore quota / private mode */
  }
}

type UserWithRelations = Prisma.UserGetPayload<{
  include: {
    company: true;
  };
}>;

interface UserContextType {
  isLoggedIn: boolean;
  hasProfile: boolean | null;
  setHasProfile: React.Dispatch<React.SetStateAction<boolean | null>>;
  userDetails: UserWithRelations | null | undefined;
  setUserDetails: React.Dispatch<React.SetStateAction<UserWithRelations | null | undefined>>;
  /** Resolves to the loaded user (or null). Use for post-login navigation from `company.status`. */
  refetchUser: () => Promise<UserWithRelations | null>;

  shortlistedIds: Set<string>;
  addToShortlist: (companyId: string) => Promise<void>;
  removeFromShortlist: (companyId: string) => Promise<void>;

  shortlistedEcosystemHeadIds: Set<string>;
  addEcosystemHeadToShortlist: (ecosystemHeadUserId: string) => Promise<void>;
  removeEcosystemHeadFromShortlist: (ecosystemHeadUserId: string) => Promise<void>;

  shortlistedExpertIds: Set<string>;
  addExpertToShortlist: (expertUserId: string) => Promise<void>;
  removeExpertFromShortlist: (expertUserId: string) => Promise<void>;

  interestedCompanyIds: Set<string>;
  hasCollaborationWith: (companyId: string) => boolean;
  addInterestedCompany: (companyId: string) => void;
  removeInterestedCompany: (companyId: string) => void;

  /** Pending collaboration requests: received = others requested you; sent = you requested others */
  pendingRequestCounts: { received: number; sent: number };
  refreshPendingInterestCount: () => void;
  newRequestsSpotlightOpen: boolean;
  acknowledgeNewRequestsSpotlight: () => void;

  collaborationSidebarCounts: { active: number; closed: number };
  refreshCollaborationSidebarCounts: () => void;

  creditBalance: number | null;
  creditBalanceLoadFailed: boolean;
  refreshCreditBalance: () => void;

  syncGuestShortlistToServer: () => Promise<void>;

  maintenanceEnabled: boolean | null;
  refetchMaintenance: () => void;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used inside UserContextProvider");
  }
  return context;
};

export const UserContextProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [userDetails, setUserDetails] = useState<UserWithRelations | null | undefined>(undefined);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState<boolean | null>(null);
  const [shortlistedIds, setShortlistedIds] = useState<Set<string>>(new Set());
  const [shortlistedEcosystemHeadIds, setShortlistedEcosystemHeadIds] = useState<Set<string>>(new Set());
  const [shortlistedExpertIds, setShortlistedExpertIds] = useState<Set<string>>(new Set());
  const [interestedCompanyIds, setInterestedCompanyIds] = useState<Set<string>>(new Set());
  const [pendingRequestCounts, setPendingRequestCounts] = useState({ received: 0, sent: 0 });
  const [newRequestsSpotlightOpen, setNewRequestsSpotlightOpen] = useState(false);
  const [collaborationSidebarCounts, setCollaborationSidebarCounts] = useState({ active: 0, closed: 0 });
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [creditBalanceLoadFailed, setCreditBalanceLoadFailed] = useState(false);
  const loginBaselineComparedRef = useRef(false);

  const isLoggedIn = !!userDetails;
  const portalRole = (() => {
    const token = Cookies.get(COOKIE_KEYS.token);
    if (!token) return null;
    try {
      return jwtDecode<{ role?: "ADMIN" | "CLIENT" | "FACILITATOR" | "SUB_ADMIN" | "ECOSYSTEM_HEAD" | "EXPERT" }>(
        token,
      )?.role ?? null;
    } catch {
      return null;
    }
  })();

  const canUseShortlist =
    portalRole === "CLIENT" ||
    portalRole === "FACILITATOR" ||
    portalRole === "ECOSYSTEM_HEAD" ||
    portalRole === "EXPERT";
  const isClientUser = portalRole === "CLIENT";

  /* ---------------- USER ---------------- */
  const fetchShortlistedIds = async () => {
    try {
      if (!canUseShortlist) {
        setShortlistedIds(new Set());
        return [];
      }
      const ids = (await trpc.user.getShortlistedIds.query()) as string[];
      setShortlistedIds(new Set(ids));
      return ids;
    } catch (err) {
      console.error("Error fetching shortlisted IDs:", err);
      return [];
    }
  };

  const fetchShortlistedEcosystemHeadIds = async () => {
    try {
      if (!isClientUser) {
        setShortlistedEcosystemHeadIds(new Set());
        return [];
      }
      const ids = (await trpc.ecosystemHead.getShortlistedEcosystemHeadIds.query()) as string[];
      setShortlistedEcosystemHeadIds(new Set(ids));
      return ids;
    } catch (err) {
      console.error("Error fetching shortlisted ecosystem head IDs:", err);
      return [];
    }
  };

  const fetchShortlistedExpertIds = async () => {
    try {
      if (!isClientUser) {
        setShortlistedExpertIds(new Set());
        return [];
      }
      const ids = (await trpc.expert.getShortlistedExpertIds.query()) as string[];
      setShortlistedExpertIds(new Set(ids));
      return ids;
    } catch (err) {
      console.error("Error fetching shortlisted expert IDs:", err);
      return [];
    }
  };

  const syncGuestShortlistToServer = useCallback(async () => {
    const pendingCompanyIds = readGuestShortlistCompanyIds();
    const pendingCorridorIds = isClientUser ? readGuestShortlistEcosystemHeadUserIds() : [];
    const pendingExpertIds = isClientUser ? readGuestShortlistExpertUserIds() : [];

    if (pendingCompanyIds.length === 0 && pendingCorridorIds.length === 0 && pendingExpertIds.length === 0) {
      return;
    }

    const currentCompanyIds = pendingCompanyIds.length > 0 ? await fetchShortlistedIds() : [];
    const currentCorridorIds =
      isClientUser && pendingCorridorIds.length > 0 ? await fetchShortlistedEcosystemHeadIds() : [];
    const currentExpertIds =
      isClientUser && pendingExpertIds.length > 0 ? await fetchShortlistedExpertIds() : [];

    const syncedCompanyIds: string[] = [];
    for (const companyId of pendingCompanyIds.filter((id) => !currentCompanyIds.includes(id))) {
      try {
        await trpc.user.shortlistCompany.mutate({ companyId });
        syncedCompanyIds.push(companyId);
      } catch (err) {
        console.error("Failed to sync guest shortlist company:", companyId, err);
      }
    }
    if (syncedCompanyIds.length > 0) {
      removeGuestShortlistCompanyIds(syncedCompanyIds);
      setShortlistedIds((prev) => new Set([...prev, ...syncedCompanyIds]));
    }
    removeGuestShortlistCompanyIds(pendingCompanyIds.filter((id) => currentCompanyIds.includes(id)));

    if (!isClientUser) return;

    const syncedCorridorIds: string[] = [];
    for (const ecosystemHeadUserId of pendingCorridorIds.filter((id) => !currentCorridorIds.includes(id))) {
      try {
        await trpc.ecosystemHead.shortlistEcosystemHead.mutate({ ecosystemHeadUserId });
        syncedCorridorIds.push(ecosystemHeadUserId);
      } catch (err) {
        console.error("Failed to sync guest shortlist corridor:", ecosystemHeadUserId, err);
      }
    }
    if (syncedCorridorIds.length > 0) {
      removeGuestShortlistEcosystemHeadUserIds(syncedCorridorIds);
      setShortlistedEcosystemHeadIds((prev) => new Set([...prev, ...syncedCorridorIds]));
    }
    removeGuestShortlistEcosystemHeadUserIds(
      pendingCorridorIds.filter((id) => currentCorridorIds.includes(id)),
    );

    const syncedExpertIds: string[] = [];
    for (const expertUserId of pendingExpertIds.filter((id) => !currentExpertIds.includes(id))) {
      try {
        await trpc.expert.shortlistExpert.mutate({ expertUserId });
        syncedExpertIds.push(expertUserId);
      } catch (err) {
        console.error("Failed to sync guest shortlist expert:", expertUserId, err);
      }
    }
    if (syncedExpertIds.length > 0) {
      removeGuestShortlistExpertUserIds(syncedExpertIds);
      setShortlistedExpertIds((prev) => new Set([...prev, ...syncedExpertIds]));
    }
    removeGuestShortlistExpertUserIds(pendingExpertIds.filter((id) => currentExpertIds.includes(id)));
  }, [isClientUser]);

  const evaluateLoginRequestBaseline = useCallback((userId: string, received: number) => {
    if (loginBaselineComparedRef.current) return;
    loginBaselineComparedRef.current = true;

    const baseline = readPendingRequestsBaseline(userId);
    if (baseline === null) {
      writePendingRequestsBaseline(userId, received);
      return;
    }
    if (received > baseline) {
      setNewRequestsSpotlightOpen(true);
    }
  }, []);

  const fetchPendingRequestCounts = useCallback(
    (options?: { compareLoginBaseline?: boolean; userId?: string }): Promise<void> => {
      // Only CLIENT users have collaboration request concepts
      if (!isClientUser || !userDetails?.company?.id) {
        setPendingRequestCounts({ received: 0, sent: 0 });
        return Promise.resolve();
      }
      return trpc.collaboration.getPendingRequestCounts
        .query()
        .then((res) => {
          setPendingRequestCounts({ received: res.received, sent: res.sent });
          if (options?.compareLoginBaseline && options.userId) {
            evaluateLoginRequestBaseline(options.userId, res.received);
          }
        })
        .catch((err) => {
          console.error("Error fetching pending request counts:", err);
        });
    },
    [evaluateLoginRequestBaseline, isClientUser, userDetails?.company?.id]
  );

  const fetchUserDetails = useCallback((): Promise<UserWithRelations | null> => {
    const token = Cookies.get(COOKIE_KEYS.token);

    if (!token) {
      loginBaselineComparedRef.current = false;
      setUserDetails(null);
      setHasProfile(false);
      setShortlistedIds(new Set());
      setShortlistedEcosystemHeadIds(new Set());
      setShortlistedExpertIds(new Set());
      setInterestedCompanyIds(new Set());
      return Promise.resolve(null);
    }

    // Was "no session" (null) → loading (undefined) so ProtectedRoute does not redirect to /login
    // before getProfile finishes. Keeps existing user object during background refetch.
    setUserDetails((prev) => (prev === null ? undefined : prev));

    return trpc.user.getProfile
      .query({})
      .then(async (res) => {
        if (!res) return null;
        const user = res as UserWithRelations;
        setUserDetails(user);
        setHasProfile(hasClientCreatedProfile(user));
        await fetchShortlistedIds();
        if (user.role === "CLIENT") {
          await fetchShortlistedEcosystemHeadIds();
          await fetchShortlistedExpertIds();
        }
        // Sync guest shortlist only after the client has a real profile (returning login),
        // not immediately after account signup before profile creation.
        if (hasClientCreatedProfile(user)) {
          await syncGuestShortlistToServer();
        }
        if (user.role === "CLIENT") {
          fetchInterestedCompanyIds();
          await fetchPendingRequestCounts({ compareLoginBaseline: true, userId: user.id });
          fetchCollaborationSidebarCounts();
        }
        return user;
      })
      .catch((err) => {
        if (isInvalidSessionTrpcError(err)) {
          invalidateClientSession();
          loginBaselineComparedRef.current = false;
          setUserDetails(null);
          setHasProfile(false);
          setShortlistedIds(new Set());
          setShortlistedEcosystemHeadIds(new Set());
          setShortlistedExpertIds(new Set());
          setInterestedCompanyIds(new Set());
          return null;
        }

        handleTrpcError(err);
        setUserDetails((prev) => {
          if (prev) {
            setHasProfile(hasClientCreatedProfile(prev));
            return prev;
          }
          setHasProfile(false);
          setShortlistedIds(new Set());
          setShortlistedEcosystemHeadIds(new Set());
          setShortlistedExpertIds(new Set());
          setInterestedCompanyIds(new Set());
          return null;
        });
        return null;
      });
  }, [syncGuestShortlistToServer, fetchPendingRequestCounts]);

  const acknowledgeNewRequestsSpotlight = useCallback(() => {
    const userId = userDetails?.id;
    if (userId) {
      writePendingRequestsBaseline(userId, pendingRequestCounts.received);
    }
    setNewRequestsSpotlightOpen(false);
  }, [userDetails?.id, pendingRequestCounts.received]);

  const fetchCollaborationSidebarCounts = useCallback(() => {
    if (!isClientUser || !userDetails?.company?.id) {
      setCollaborationSidebarCounts({ active: 0, closed: 0 });
      return;
    }
    trpc.collaboration.getCollaborationSidebarCounts
      .query()
      .then((res) => {
        setCollaborationSidebarCounts({ active: res.active, closed: res.closed });
      })
      .catch((err) => {
        console.error("Error fetching collaboration sidebar counts:", err);
      });
  }, [isClientUser, userDetails?.company?.id]);

  const refreshCreditBalance = useCallback(() => {
    if (!isClientUser || !userDetails?.id) {
      setCreditBalance(null);
      setCreditBalanceLoadFailed(false);
      return;
    }
    trpc.wallet.getBalance
      .query()
      .then((res) => {
        setCreditBalance(res.available);
        setCreditBalanceLoadFailed(false);
      })
      .catch((err) => {
        handleTrpcError(err);
        setCreditBalanceLoadFailed(true);
        setCreditBalance(null);
      });
  }, [isClientUser, userDetails?.id]);

  const fetchInterestedCompanyIds = () => {
    if (!isClientUser || !userDetails?.company?.id) {
      setInterestedCompanyIds(new Set());
      return;
    }
    trpc.collaboration.getInterestedCompanyIds
      .query()
      .then((companyIds: string[]) => {
        setInterestedCompanyIds(new Set(companyIds));
      })
      .catch((err) => {
        console.error("Error fetching interested company IDs:", err);
      });
  };

  const hasCollaborationWith = (companyId: string) => {
    return interestedCompanyIds.has(companyId);
  };

  const addInterestedCompany = (companyId: string) => {
    setInterestedCompanyIds((prev) => new Set([...prev, companyId]));
  };

  const removeInterestedCompany = (companyId: string) => {
    setInterestedCompanyIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(companyId);
      return newSet;
    });
  };

  const addToShortlist = async (companyId: string) => {
    if (!canUseShortlist) return;
    await trpc.user.shortlistCompany.mutate({ companyId });
    setShortlistedIds((prev) => new Set([...prev, companyId]));
  };

  const removeFromShortlist = async (companyId: string) => {
    if (!canUseShortlist) return;
    await trpc.user.removeShortlist.mutate({ companyId });
    setShortlistedIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(companyId);
      return newSet;
    });
  };

  const addEcosystemHeadToShortlist = async (ecosystemHeadUserId: string) => {
    if (!isClientUser) return;
    await trpc.ecosystemHead.shortlistEcosystemHead.mutate({ ecosystemHeadUserId });
    setShortlistedEcosystemHeadIds((prev) => new Set([...prev, ecosystemHeadUserId]));
  };

  const removeEcosystemHeadFromShortlist = async (ecosystemHeadUserId: string) => {
    if (!isClientUser) return;
    await trpc.ecosystemHead.removeEcosystemHeadShortlist.mutate({ ecosystemHeadUserId });
    setShortlistedEcosystemHeadIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(ecosystemHeadUserId);
      return newSet;
    });
  };

  const addExpertToShortlist = async (expertUserId: string) => {
    if (!isClientUser) return;
    await trpc.expert.shortlistExpert.mutate({ expertUserId });
    setShortlistedExpertIds((prev) => new Set([...prev, expertUserId]));
  };

  const removeExpertFromShortlist = async (expertUserId: string) => {
    if (!isClientUser) return;
    await trpc.expert.removeExpertShortlist.mutate({ expertUserId });
    setShortlistedExpertIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(expertUserId);
      return newSet;
    });
  };

  const logout = () => {
    invalidateClientSession();
    loginBaselineComparedRef.current = false;
    setUserDetails(null);
    setHasProfile(null);
    setShortlistedIds(new Set());
    setShortlistedEcosystemHeadIds(new Set());
    setShortlistedExpertIds(new Set());
    setInterestedCompanyIds(new Set());
    setNewRequestsSpotlightOpen(false);
    setCreditBalance(null);
    setCreditBalanceLoadFailed(false);
    navigate("/login");
  };

  /* ---------------- MAINTENANCE ---------------- */
  const fetchMaintenance = () => {
    trpc.maintenance.get
      .query()
      .then((res) => {
        setMaintenanceEnabled(!!res?.webMaintenanceEnabled);
      })
      .catch((err) => {
        console.error("Error fetching maintenance:", err);
        setMaintenanceEnabled(false);
      });
  };

  /* ---------------- REDIRECT LOGIC ---------------- */
  useEffect(() => {
    if (maintenanceEnabled === null) return;

    // If maintenance is ON → force maintenance page
    if (maintenanceEnabled && location.pathname !== "/maintenance") {
      navigate("/maintenance", { replace: true });
    }

    // If maintenance is OFF → prevent staying on maintenance page
    if (!maintenanceEnabled && location.pathname === "/maintenance") {
      navigate("/login", { replace: true });
    }
  }, [maintenanceEnabled, location.pathname, navigate]);

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    fetchMaintenance();
    fetchUserDetails();
  }, []);

  return (
    <UserContext.Provider
      value={{
        isLoggedIn,
        hasProfile,
        setHasProfile,
        userDetails,
        setUserDetails,
        refetchUser: fetchUserDetails,
        shortlistedIds,
        addToShortlist,
        removeFromShortlist,
        shortlistedEcosystemHeadIds,
        addEcosystemHeadToShortlist,
        removeEcosystemHeadFromShortlist,
        shortlistedExpertIds,
        addExpertToShortlist,
        removeExpertFromShortlist,
        interestedCompanyIds,
        hasCollaborationWith,
        addInterestedCompany,
        removeInterestedCompany,
        pendingRequestCounts,
        refreshPendingInterestCount: () => fetchPendingRequestCounts(),
        newRequestsSpotlightOpen,
        acknowledgeNewRequestsSpotlight,
        collaborationSidebarCounts,
        refreshCollaborationSidebarCounts: fetchCollaborationSidebarCounts,
        creditBalance,
        creditBalanceLoadFailed,
        refreshCreditBalance,
        syncGuestShortlistToServer,
        maintenanceEnabled,
        refetchMaintenance: fetchMaintenance,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserProvider = UserContextProvider;
export const useUser = useUserContext;
